import { Edit, InfoOutline } from '@mui/icons-material';
import {
  Box,
  Breakpoint,
  IconButton,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, PropsWithChildren, ReactNode } from 'react';
import Dot from '../Dot';
import { Column, Row } from '../Layout';

type FormFieldSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const sizesMap: Record<FormFieldSize, string> = {
  xxs: '80px',
  xs: '120px',
  sm: '220px',
  md: '400px',
  lg: '500px',
  xl: '600px',
  xxl: '700px',
};

const labelSizesMap: Record<FormFieldSize, string> = {
  xxs: '100px',
  xs: '150px',
  sm: '210px',
  md: '290px',
  lg: '320px',
  xl: '400px',
  xxl: '450px',
};

const EditButton = styled(IconButton)({ padding: 0 });
const EditIcon = styled(Edit)({
  width: 16,
  height: 16,
});

const ColumnWrapper = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
}));

const RowWrapper = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
  alignItems: 'flex-start',
}));

const LabelColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'labelSize' && prop !== 'isHorizontal',
})<{ labelSize?: FormFieldSize; isHorizontal?: boolean }>(
  ({ theme, labelSize, isHorizontal }) => ({
    flex:
      isHorizontal && labelSize
        ? `0 0 ${labelSizesMap[labelSize]}`
        : '0 0 auto',
    ...(isHorizontal && {
      paddingTop: theme.spacing(theme.spacingSizes.xs),
    }),
  }),
);

const ChildrenWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'isHorizontal',
})<{ size?: FormFieldSize; isHorizontal?: boolean }>(
  ({ size, isHorizontal }) => ({
    width: '100%',
    ...(isHorizontal && size && { maxWidth: sizesMap[size] }),
  }),
);

type Props = {
  label?: string | ReactNode;
  tooltip?: ReactNode;
  extraInformation?: string;
  size?: FormFieldSize;
  labelColorLight?: boolean;
  spacing?: keyof Theme['spacingSizes'];
  editable?: boolean;
  onEdit?: () => void;
  dotColor?: string;
  dotTooltipText?: string;
  layout?: 'horizontal' | 'vertical';
  labelSize?: FormFieldSize;
  breakpoint?: Breakpoint;
};

const FormField: FC<PropsWithChildren<Props>> = ({
  children,
  label,
  extraInformation,
  size,
  labelColorLight,
  tooltip,
  spacing = 'sm',
  editable = false,
  onEdit,
  dotColor,
  dotTooltipText,
  layout = 'vertical',
  labelSize,
  breakpoint,
}) => {
  const theme = useTheme();
  const isCollapsed =
    useMediaQuery(theme.breakpoints.down(breakpoint ?? 'xs')) && !!breakpoint;
  const isHorizontal = layout === 'horizontal' && !isCollapsed;
  const Wrapper = isHorizontal ? RowWrapper : ColumnWrapper;
  return (
    <Wrapper
      size={isHorizontal ? undefined : size}
      spacing={spacing}
      as={isHorizontal ? Row : Column}
    >
      {!!label &&
        (typeof label === 'string' ? (
          <LabelColumn
            spacing="xs"
            labelSize={labelSize}
            isHorizontal={isHorizontal}
          >
            <Row centered spacing="xxs">
              <Typography
                color={labelColorLight ? 'grey.600' : undefined}
                variant="h6"
              >
                {label}
              </Typography>
              {tooltip ? (
                <Tooltip
                  title={tooltip}
                  slotProps={{ popper: { disablePortal: true } }}
                >
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              ) : null}
              {editable ? (
                <EditButton size="small" onClick={onEdit}>
                  <EditIcon />
                </EditButton>
              ) : null}
              {dotColor ? (
                <Tooltip
                  title={dotTooltipText}
                  slotProps={{ popper: { disablePortal: true } }}
                >
                  <Dot color={dotColor} showMargin />
                </Tooltip>
              ) : null}
            </Row>
            {!!extraInformation && (
              <Typography variant="body2">{extraInformation}</Typography>
            )}
          </LabelColumn>
        ) : (
          label
        ))}

      <ChildrenWrapper size={size} isHorizontal={isHorizontal}>
        {children}
      </ChildrenWrapper>
    </Wrapper>
  );
};

export default FormField;
